import { get, postJson, patch, deleteRequest } from './apiClient';

export interface ConfirmedOrderDto {
  _id: string;
  rfqId?: string | null;
  rfqNumber?: string;
  quotationId?: string | null;
  quotationNumber?: string;
  clientName: string;
  clientEmail?: string;
  vesselName?: string;
  items?: any[];
  vendors?: string[];
  totalItems: number;
  totalQuote: number;
  currency?: string;
  /** AED -> currency snapshot rate; multiply an AED-base amount by this to display. */
  fxRate?: number;
  status: string;
  confirmedAt: string;
  confirmationSource: string;
  emailFrom?: string;
  emailSubject?: string;
  notes?: string;
  linkStatus?: 'linked' | 'pending_manual';
  matchScore?: number | null;
  matchNotes?: string;
  candidateQuotationIds?: string[];
}

export interface ConfirmedOrderListResponse {
  success: boolean;
  data: {
    confirmedOrders: ConfirmedOrderDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface ConfirmedOrderDetailResponse {
  success: boolean;
  data: {
    confirmedOrder: ConfirmedOrderDto;
    quotation: any;
    rfq: any;
    vendorPurchaseOrders: any[];
  };
}

export async function fetchConfirmedOrders(page = 1, limit = 50, search = '', lean = false) {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (lean) query.set('lean', '1'); // drop items[] — used by the dashboard board
  const res = await get<ConfirmedOrderListResponse>(`/confirmed-orders?${query.toString()}`);
  return res.data;
}

export async function fetchConfirmedOrderById(id: string) {
  const res = await get<ConfirmedOrderDetailResponse>(`/confirmed-orders/${id}`);
  return res.data;
}

export async function manualConfirmOrder(payload: { quotationId?: string; rfqId?: string; notes?: string }) {
  const res = await postJson<{ success: boolean; data: ConfirmedOrderDto }>(`/confirmed-orders/manual`, payload);
  return res.data;
}

export async function linkConfirmedOrder(id: string, quotationId: string) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/link`,
    { quotationId }
  );
  return res.data;
}

export async function updateConfirmedOrder(
  id: string,
  payload: Partial<Pick<ConfirmedOrderDto, 'notes' | 'clientName' | 'clientEmail' | 'vesselName' | 'status'>>
) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(`/confirmed-orders/${id}`, payload);
  return res.data;
}

export async function deleteConfirmedOrderItem(id: string, index: number) {
  const res = await deleteRequest<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/items/${index}`
  );
  return res.data;
}

export async function reconcileConfirmedOrder(
  id: string,
  payload: { finalItems: any[]; note?: string }
) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/reconcile`,
    payload
  );
  return res.data;
}

export async function deleteAllConfirmedOrders() {
  const res = await deleteRequest<{ success: boolean; data: { deleted: number } }>(
    `/confirmed-orders/all`
  );
  return res.data;
}

// Post-PO vendor-short: can the margin absorb a revised (higher) vendor cost?
export async function assessPostPo(id: string, lineNumber: number, revisedUnitCost: number) {
  const res = await postJson<{ success: boolean; data: any }>(
    `/confirmed-orders/${id}/post-po/assess`,
    { lineNumber, revisedUnitCost }
  );
  return res.data;
}

// Post-PO vendor-short: apply a resolution to the short line.
export async function resolvePostPo(
  id: string,
  lineNumber: number,
  opts: {
    resolution: 'flag' | 'absorb' | 'escalate' | 'cancel';
    revisedUnitCost?: number;
    revisedSellingPrice?: number;
    cancelQty?: number;
  }
) {
  const res = await postJson<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/post-po/resolve`,
    { lineNumber, ...opts }
  );
  return res.data;
}


export async function changeConfirmedOrderItemSupplier(
  id: string,
  itemIndex: number,
  vendorName: string
) {
  const res = await patch<{ success: boolean; data: ConfirmedOrderDto }>(
    `/confirmed-orders/${id}/items/${itemIndex}/supplier`,
    { vendorName }
  );
  return res.data;
}
